import { CoreError, CoreErrorCause } from "../../../domain/models/CoreError";

export enum UserErrorCause {
  UserNotFound = "user_not_found",
  UserAlreadyExists = "user_already_exists",
  InvalidUserData = "invalid_user_data",
  PermissionDenied = "permission_denied",
  NetworkError = "network_error",
}

interface CreateUserErrorOptions {
  reportToSentry?: boolean;
}

export class UserErrorConverter {
  /**
   * Converts Firebase Database errors to CoreError with user-specific causes.
   */
  static domainModel(error: unknown): CoreError {
    if (error instanceof CoreError) {
      return error;
    }

    // Firebase Database error codes
    const err = (error ?? {}) as { code?: string; message?: string };
    const code = err.code || "";
    const message = err.message || "Unknown error";

    switch (code) {
      case "DATABASE/PERMISSION-DENIED":
      case "PERMISSION_DENIED":
        return new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: `Permission denied: ${message}`,
          originalError: error,
        });

      case "DATABASE/NETWORK-ERROR":
      case "NETWORK_ERROR":
        return new CoreError({
          cause: CoreErrorCause.NetworkError,
          context: `Network error: ${message}`,
          originalError: error,
        });

      case "DATABASE/UNAVAILABLE":
        return new CoreError({
          cause: CoreErrorCause.NetworkError,
          context: `Database unavailable: ${message}`,
          originalError: error,
        });

      case "DATABASE/DATA-STALE":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Stale data: ${message}`,
          originalError: error,
        });

      case "DATABASE/OPERATION-FAILED":
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `Operation failed: ${message}`,
          originalError: error,
        });

      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: message,
          originalError: error,
        });
    }
  }

  /**
   * Creates a user-specific error.
   */
  static createUserError(
    cause: UserErrorCause,
    context: string,
    options?: CreateUserErrorOptions,
  ): CoreError {
    let coreErrorCause: CoreErrorCause;

    switch (cause) {
      case UserErrorCause.UserNotFound:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;
      case UserErrorCause.UserAlreadyExists:
        coreErrorCause = CoreErrorCause.Unknown;
        break;
      case UserErrorCause.InvalidUserData:
        coreErrorCause = CoreErrorCause.DataNotFound;
        break;
      case UserErrorCause.PermissionDenied:
        coreErrorCause = CoreErrorCause.PermissionDenied;
        break;
      case UserErrorCause.NetworkError:
        coreErrorCause = CoreErrorCause.NetworkError;
        break;
      default:
        coreErrorCause = CoreErrorCause.Unknown;
    }

    return new CoreError({
      cause: coreErrorCause,
      context,
      reportToSentry: options?.reportToSentry,
    });
  }
}
